import React from 'react';
import { useLocation } from 'react-router-dom';
import { Utensils, Plus } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/shared/components/ui/Button';
import { buildRoute } from '@/shared/lib/constants';
import { TabNavigation } from '@/features/shared/components/TabNavigation';
import type { Tab } from '@/features/shared/components/TabNavigation';
import { PondFilter } from '@/features/shared/components/PondFilter';
import type { PondOption } from '@/features/shared/components/PondFilter';
import { useFarmFromUrl } from '@/shared/hooks/useUrlSync';
import { useFeaturePageState } from '@/shared/hooks/useFeaturePageState';
import { PageLoading } from '@/shared/components/ui/LoadingSpinner';
import { FarmRequiredGuard } from '@/shared/components/FarmRequiredGuard';
import { FeedRecordsTab } from './FeedRecordsTab';
import { FeedTrendsTab } from './FeedTrendsTab';
import { FeedAlertsTab } from './FeedAlertsTab';
import { FeedExportButton } from './FeedExportButton';

type TabId = 'records' | 'trends' | 'alerts';

const tabs: Tab[] = [
  { id: 'records', label: 'Feed Records' },
  { id: 'trends', label: 'Trends & Analytics' },
  { id: 'alerts', label: 'Alerts' },
];

// Tab configuration for feed feature (path-based routing)
const FEED_TABS = [
  { id: 'records', path: '' },
  { id: 'trends', path: '/trends' },
  { id: 'alerts', path: '/alerts' },
];

export const FeedPage: React.FC = () => {
  const { farmId, farm, isLoading: isFarmLoading } = useFarmFromUrl();
  const location = useLocation();
  const navigate = useNavigate();

  // Determine active tab from URL path (path-based routing)
  const getActiveTab = (): TabId => {
    if (location.pathname.endsWith('/trends')) return 'trends';
    if (location.pathname.endsWith('/alerts')) return 'alerts';
    return 'records';
  };
  const activeTab = getActiveTab();

  // Use shared hook for pond selection and tab navigation
  // This handles: pond fetching, URL sync, localStorage restoration, and tab navigation
  const {
    selectedPondId,
    setSelectedPondId,
    navigateToTab,
    ponds,
    isPondsLoading,
  } = useFeaturePageState({
    farmId,
    feature: 'feed',
    tabs: FEED_TABS,
    currentTab: activeTab,
  });

  // Show loading state
  if (isFarmLoading || (isPondsLoading && ponds.length === 0)) {
    return <PageLoading message="Loading..." />;
  }

  // Show farm selection guard if no farmId
  if (!farmId) {
    return (
      <FarmRequiredGuard farmId={farmId} isLoading={isFarmLoading}>
        {null}
      </FarmRequiredGuard>
    );
  }

  const pondOptions: PondOption[] = ponds.map((pond) => ({
    id: pond.id,
    name: pond.name,
  }));

  return (
    <div className="space-y-6">
      {/* Ponds Loading Banner */}
      {isPondsLoading && (
        <div className="rounded-lg bg-emerald-50 border border-emerald-200/50 p-4">
          <p className="font-roboto text-sm text-emerald-900">Loading ponds...</p>
        </div>
      )}

      {/* Page Header */}
      <div>
        <div className="flex items-center gap-2 text-sm text-gray-600 mb-2">
          <span>{farm?.name}</span>
          <span>›</span>
          <span className="text-gray-900 font-medium">Feed Management</span>
        </div>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="p-3 bg-emerald-50 rounded-lg border border-emerald-200/50">
              <Utensils className="h-6 w-6 text-emerald-600" />
            </div>
            <div>
              <h1 className="font-roboto text-2xl font-bold text-gray-900">Feed Management</h1>
              <p className="font-roboto mt-1 text-sm text-gray-600">
                {selectedPondId && activeTab === 'records'
                  ? `Feed records for ${ponds.find((p) => p.id === selectedPondId)?.name ?? 'selected pond'}`
                  : activeTab === 'trends'
                  ? 'Track feeding trends and efficiency'
                  : activeTab === 'alerts'
                  ? 'Monitor feeding alerts and issues'
                  : 'Monitor feeding schedules, appetite, and feed efficiency'}
              </p>
            </div>
          </div>
          {activeTab === 'records' && (
            <div className="flex items-center gap-2">
              <Button
                variant="primary"
                onClick={() => navigate(buildRoute.feedNew(farmId, selectedPondId))}
                disabled={!selectedPondId}
                title={!selectedPondId ? "Select a pond first" : undefined}
              >
                <Plus className="h-4 w-4 mr-1" />
                Add Record
              </Button>
              <FeedExportButton
                farmId={farmId}
                pondId={selectedPondId}
                pondName={ponds.find((p) => p.id === selectedPondId)?.name}
                disabled={!selectedPondId}
              />
            </div>
          )}
        </div>
      </div>

      {/* Tab Navigation */}
      <TabNavigation tabs={tabs} activeTab={activeTab} onChange={navigateToTab} />

      {/* Pond Filter (only show on Records tab) */}
      {activeTab === 'records' && ponds.length > 0 && (
        <div className="max-w-xs">
          <PondFilter
            ponds={pondOptions}
            selectedPondId={selectedPondId}
            onChange={setSelectedPondId}
            label="Filter by Pond"
          />
        </div>
      )}

      {/* Tab Content */}
      {activeTab === 'records' ? (
        <FeedRecordsTab farmId={farmId} selectedPondId={selectedPondId} />
      ) : activeTab === 'trends' ? (
        <FeedTrendsTab farmId={farmId} ponds={ponds} />
      ) : (
        <FeedAlertsTab farmId={farmId} />
      )}
    </div>
  );
};
